/**
 * EmailCampaignsPage — list, compose, send, and audit broadcast campaigns.
 *
 * Three panes:
 *  1. Left: list of recent campaigns (status pill, recipient count)
 *  2. Center: composer for a draft (subject / headline / body / CTA / audience)
 *  3. Right: audience preview + send + stats
 *
 * Pure read/write against /api/email/campaigns/* — no client-side fan-out.
 * Aud filtering happens server-side; suppression list (unsubscribes) is
 * applied at preview AND at enqueue time so we never violate consent.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Send, Users, Tag, PlusCircle, Trash2, Mail, Eye, RefreshCw,
  CheckCircle2, AlertCircle, ChevronLeft,
} from "lucide-react";
import { api } from "../../lib/api";

const AUDIENCE_KINDS = [
  { id: "all", label: "All customers" },
  { id: "status", label: "By status" },
  { id: "tags", label: "By tag" },
  { id: "emails", label: "Specific emails" },
];

const STATUS_PILL = {
  draft: "bg-slate-100 text-slate-600",
  scheduled: "bg-blue-50 text-blue-700",
  sending: "bg-amber-50 text-amber-700",
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-rose-50 text-rose-700",
};

const EMPTY_DRAFT = {
  name: "",
  subject: "",
  headline: "",
  body_html: "",
  body_text: "",
  cta_url: "",
  cta_label: "Open",
  audience: { kind: "all" },
};

export default function EmailCampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const selected = useMemo(
    () => campaigns.find((c) => c.id === selectedId),
    [campaigns, selectedId]
  );

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/email/campaigns?limit=50");
      setCampaigns(r.data || []);
    } catch (e) {
      toast.error("Could not load campaigns");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // When a campaign is selected, load it into the composer.
  useEffect(() => {
    if (!selectedId) return;
    api.get(`/email/campaigns/${selectedId}`).then((r) => {
      const d = r.data;
      setDraft({
        name: d.name || "",
        subject: d.subject || "",
        headline: d.headline || "",
        body_html: d.body_html || "",
        body_text: d.body_text || "",
        cta_url: d.cta_url || "",
        cta_label: d.cta_label || "Open",
        audience: d.audience || { kind: "all" },
      });
      setPreview(null);
    }).catch(() => toast.error("Failed to load campaign"));
  }, [selectedId]);

  const newDraft = () => {
    setSelectedId(null);
    setDraft(EMPTY_DRAFT);
    setPreview(null);
  };

  const saveDraft = async () => {
    if (!draft.name?.trim() || !draft.subject?.trim()) {
      toast.error("Name and subject are required");
      return;
    }
    setSaving(true);
    try {
      if (selectedId) {
        await api.patch(`/email/campaigns/${selectedId}`, draft);
        toast.success("Campaign updated");
      } else {
        const r = await api.post("/email/campaigns", draft);
        setSelectedId(r.data.id);
        toast.success("Campaign created");
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const previewAudience = async () => {
    if (!selectedId) {
      toast.error("Save the campaign first");
      return;
    }
    try {
      const r = await api.post(`/email/campaigns/${selectedId}/audience-preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const sendNow = async () => {
    if (!selectedId) return;
    if (!window.confirm("Send this campaign now?")) return;
    setSending(true);
    try {
      const r = await api.post(`/email/campaigns/${selectedId}/send`);
      toast.success(`Enqueued ${r.data.enqueued} sends · ${r.data.skipped_unsubscribed} skipped`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const deleteCampaign = async () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this campaign? This cannot be undone.")) return;
    try {
      await api.delete(`/email/campaigns/${selectedId}`);
      toast.success("Campaign deleted");
      newDraft();
      await refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const isLocked = selected && !["draft", "scheduled", "failed"].includes(selected.status);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="email-campaigns-page">
      <div className="max-w-[1280px] mx-auto p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/settings?tab=email")}
              className="rounded-full border border-line bg-white px-3 py-1.5 text-xs text-meta hover:text-ink inline-flex items-center gap-1"
              data-testid="campaigns-back-btn"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Back to email
            </button>
            <h1 className="font-display text-2xl font-bold text-ink">Email campaigns</h1>
          </div>
          <button
            type="button"
            onClick={newDraft}
            data-testid="campaign-new-btn"
            className="rounded-full ashra-gradient-bg px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md transition"
          >
            <PlusCircle className="h-4 w-4" /> New campaign
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_320px] gap-6">
          {/* ─── Left: list ─── */}
          <aside className="surface p-3 max-h-[80vh] overflow-y-auto" data-testid="campaigns-list">
            {campaigns.length === 0 ? (
              <p className="text-xs text-meta p-3">No campaigns yet — click <strong>New campaign</strong>.</p>
            ) : (
              campaigns.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  data-testid={`campaign-row-${c.id}`}
                  className={`w-full text-left rounded-lg p-3 mb-1.5 border ${
                    selectedId === c.id
                      ? "border-ai/40 bg-violet-50/40"
                      : "border-transparent hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-ink truncate">{c.name}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_PILL[c.status] || "bg-slate-100"}`}>
                      {c.status}
                    </span>
                  </div>
                  <p className="text-xs text-meta truncate mt-0.5">{c.subject || "(no subject)"}</p>
                  <p className="text-[11px] text-meta-soft mt-1">
                    {(c.stats?.sent || 0)} sent · {(c.stats?.recipients || 0)} recipients
                  </p>
                </button>
              ))
            )}
          </aside>

          {/* ─── Center: composer ─── */}
          <section className="surface p-6" data-testid="campaign-composer">
            <h2 className="font-display text-lg font-bold text-ink mb-4 inline-flex items-center gap-2">
              <Mail className="h-4 w-4 text-ai" />
              {selectedId ? "Edit campaign" : "New campaign"}
              {isLocked && (
                <span className="ml-2 text-xs text-meta font-normal">(read-only — already {selected.status})</span>
              )}
            </h2>
            <div className="space-y-4">
              <Field label="Campaign name" required>
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  disabled={isLocked}
                  data-testid="campaign-name-input"
                  placeholder="May launch announcement"
                  className="input"
                />
              </Field>
              <Field label="Subject line" required hint="Supports {{name}} merge tags">
                <input
                  type="text"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  disabled={isLocked}
                  data-testid="campaign-subject-input"
                  className="input"
                />
              </Field>
              <Field label="Headline">
                <input
                  type="text"
                  value={draft.headline}
                  onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
                  disabled={isLocked}
                  data-testid="campaign-headline-input"
                  className="input"
                />
              </Field>
              <Field label="Body (HTML)" hint="Plain HTML — basic markup is fine">
                <textarea
                  rows={6}
                  value={draft.body_html}
                  onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                  disabled={isLocked}
                  data-testid="campaign-body-input"
                  className="input font-mono text-xs"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="CTA URL">
                  <input
                    type="url"
                    value={draft.cta_url}
                    onChange={(e) => setDraft({ ...draft, cta_url: e.target.value })}
                    disabled={isLocked}
                    data-testid="campaign-cta-url-input"
                    placeholder="https://"
                    className="input"
                  />
                </Field>
                <Field label="CTA label">
                  <input
                    type="text"
                    value={draft.cta_label}
                    onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })}
                    disabled={isLocked}
                    data-testid="campaign-cta-label-input"
                    className="input"
                  />
                </Field>
              </div>

              {/* Audience */}
              <div className="rounded-xl border border-line bg-white p-4">
                <p className="text-sm font-semibold text-ink mb-3 inline-flex items-center gap-1.5">
                  <Users className="h-4 w-4 text-ai" /> Audience
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <select
                    value={draft.audience?.kind || "all"}
                    onChange={(e) => setDraft({ ...draft, audience: { kind: e.target.value } })}
                    disabled={isLocked}
                    data-testid="campaign-audience-kind"
                    className="input"
                  >
                    {AUDIENCE_KINDS.map((k) => (
                      <option key={k.id} value={k.id}>{k.label}</option>
                    ))}
                  </select>
                  {draft.audience?.kind === "status" && (
                    <select
                      value={draft.audience.status || ""}
                      onChange={(e) => setDraft({ ...draft, audience: { ...draft.audience, status: e.target.value } })}
                      disabled={isLocked}
                      data-testid="campaign-audience-status"
                      className="input"
                    >
                      <option value="">— pick —</option>
                      <option value="lead">Leads</option>
                      <option value="buyer">Buyers</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  )}
                  {draft.audience?.kind === "tags" && (
                    <input
                      type="text"
                      placeholder="vip, webinar_attendee (comma-sep)"
                      onChange={(e) => setDraft({
                        ...draft,
                        audience: {
                          kind: "tags",
                          tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean),
                        },
                      })}
                      disabled={isLocked}
                      data-testid="campaign-audience-tags"
                      className="input"
                    />
                  )}
                  {draft.audience?.kind === "emails" && (
                    <input
                      type="text"
                      placeholder="a@x.com, b@y.com"
                      onChange={(e) => setDraft({
                        ...draft,
                        audience: {
                          kind: "emails",
                          emails: e.target.value.split(",").map((t) => t.trim()).filter(Boolean),
                        },
                      })}
                      disabled={isLocked}
                      data-testid="campaign-audience-emails"
                      className="input"
                    />
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={saveDraft}
                  disabled={saving || isLocked}
                  data-testid="campaign-save-btn"
                  className="rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50 hover:bg-ink/90 transition"
                >
                  {saving ? "Saving…" : selectedId ? "Save changes" : "Save draft"}
                </button>
                {selectedId && (
                  <button
                    type="button"
                    onClick={deleteCampaign}
                    disabled={selected?.status === "sending"}
                    data-testid="campaign-delete-btn"
                    className="rounded-full border border-line bg-white px-3 py-2 text-sm text-rose-600 inline-flex items-center gap-1 disabled:opacity-50 hover:bg-rose-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* ─── Right: send + stats ─── */}
          <aside className="surface p-6 space-y-5" data-testid="campaign-send-panel">
            <div>
              <p className="text-sm font-semibold text-ink mb-2 inline-flex items-center gap-1.5">
                <Eye className="h-4 w-4 text-ai" /> Audience preview
              </p>
              <button
                type="button"
                onClick={previewAudience}
                disabled={!selectedId}
                data-testid="campaign-preview-btn"
                className="w-full rounded-full border border-line bg-white px-3 py-2 text-sm text-ink-soft hover:bg-slate-50 disabled:opacity-50 inline-flex items-center justify-center gap-1"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh count
              </button>
              {preview && (
                <div className="mt-3 rounded-xl border border-line bg-white p-3 text-xs space-y-1.5" data-testid="campaign-preview-result">
                  <div className="flex justify-between"><span className="text-meta">Total in audience</span><span className="font-semibold text-ink">{preview.total}</span></div>
                  <div className="flex justify-between"><span className="text-meta">Eligible to send</span><span className="font-semibold text-emerald-600">{preview.eligible}</span></div>
                  <div className="flex justify-between"><span className="text-meta">Suppressed</span><span className="font-semibold text-rose-600">{preview.suppressed}</span></div>
                  {preview.sample?.length > 0 && (
                    <div className="border-t border-line/60 pt-2 mt-2">
                      <p className="text-[11px] text-meta-soft mb-1">Sample recipients:</p>
                      {preview.sample.map((s) => (
                        <p key={s} className="text-[11px] text-ink-soft truncate">{s}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <button
                type="button"
                onClick={sendNow}
                disabled={!selectedId || isLocked || sending}
                data-testid="campaign-send-btn"
                className="w-full rounded-full ashra-gradient-bg px-4 py-2.5 text-sm font-semibold text-white inline-flex items-center justify-center gap-1 shadow-sm hover:shadow-md disabled:opacity-50 transition"
              >
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send now"}
              </button>
              <p className="text-[11px] text-meta mt-1.5 text-center">
                Unsubscribed recipients are filtered automatically.
              </p>
            </div>

            {selected && (
              <div className="rounded-xl border border-line bg-white p-3">
                <p className="text-xs font-semibold text-ink mb-2">Live stats</p>
                <div className="text-xs space-y-1" data-testid="campaign-stats">
                  <Row label="Status">
                    <span className={`pill ${selected.status === "sent" ? "pill-ai" : ""}`}>
                      {selected.status}
                    </span>
                  </Row>
                  <Row label="Recipients">{selected.stats?.recipients || 0}</Row>
                  <Row label="Sent">{selected.stats?.sent || 0}</Row>
                  <Row label="Delivered">{selected.stats?.delivered || 0}</Row>
                  <Row label="Skipped (unsubscribed)">{selected.stats?.skipped || 0}</Row>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      <style>{`
        .input { width:100%; border-radius:10px; background:#FFFFFF; border:1px solid var(--line, #E2E8F0); padding:.55rem .75rem; font-size:.875rem; color:#0F172A; outline:none; }
        .input:focus { box-shadow:0 0 0 3px rgba(139,92,246,.18); border-color:#8B5CF6; }
        .input:disabled { background:#F8FAFC; color:#64748B; }
      `}</style>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ink-soft uppercase tracking-wider">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      {hint && <span className="block text-[11px] text-meta-soft mb-1">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-meta">{label}</span>
      <span className="font-medium text-ink">{children}</span>
    </div>
  );
}
